import { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";
import { ImageFill } from "../../../components/image-fill";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import EditIcon from "@mui/icons-material/Edit";

// Helper function to detect if a file is a PDF based on URL/path or file type
const isPdfFile = (url: string, fileType?: string): boolean => {
  // First check if we have explicit file type info
  if (fileType) {
    return fileType === 'application/pdf' || fileType.toLowerCase().includes('pdf');
  }
  
  // Fall back to URL checking (for already-uploaded files)
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  return lowerUrl.endsWith('.pdf') || lowerUrl.includes('.pdf?') || lowerUrl.includes('application/pdf');
};

export const DesignerFileImageDisplay = ({
  src,
  fileType,
  isCover = false,
  onDelete,
  onSetCover,
  onEditDetails,
  sx,
}: {
  src: string;
  fileType?: string;
  isCover?: boolean;
  onDelete?: () => Promise<void> | void;
  onSetCover?: () => void;
  onEditDetails?: () => void;
  sx?: SxProps;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const hasMenu = Boolean(onDelete || (onSetCover && !isCover) || onEditDetails);

  const handleDelete = async () => {
    if (!onDelete) return;
    setIsLoading(true);
    try {
      await onDelete();
    } catch (error) {
      console.error("Error deleting file:", error);
    } finally {
      setIsLoading(false);
      setMenuAnchor(null);
    }
  };

  const handleSetCover = () => {
    if (!onSetCover) return;
    onSetCover();
    setMenuAnchor(null);
  };

  const handleEditDetails = () => {
    if (!onEditDetails) return;
    onEditDetails();
    setMenuAnchor(null);
  };

  return (
    <>
      <Box sx={{ position: "relative", borderRadius: "8px", overflow: "hidden" }}>
        {isCover && (
          <Chip
            icon={<StarIcon sx={{ fontSize: 16, color: "#FFC107" }} />}
            label="Cover Photo"
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              backgroundColor: "rgba(0,0,0,0.75)",
              color: "#fff",
              zIndex: 2,
              "& .MuiChip-icon": { color: "#FFC107" },
            }}
          />
        )}
        {hasMenu && (
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "rgba(255,255,255,0.95)",
              boxShadow: "0px 4px 8px rgba(0,0,0,0.15)",
              zIndex: 2,
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setMenuAnchor(event.currentTarget);
            }}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        )}

        {isPdfFile(src, fileType) ? (
          <Box
            component="a"
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "inherit",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#F5F5F5",
              textDecoration: "none",
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "#EEEEEE",
              },
              ...sx,
            }}
          >
            <Stack spacing={1} alignItems="center">
              <PictureAsPdfIcon sx={{ fontSize: 48, color: "#D32F2F" }} />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                PDF File
              </Typography>
            </Stack>
          </Box>
        ) : (
          <ImageFill
            src={src}
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "inherit",
              ...sx,
            }}
          />
        )}

        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3,
            }}
          >
            <CircularProgress sx={{ color: "#00A06D" }} size={32} />
          </Box>
        )}
      </Box>

      <Menu
        id={`menu-${src}`}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {onEditDetails && (
          <MenuItem onClick={handleEditDetails}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Edit Details
          </MenuItem>
        )}
        {!isCover && onSetCover && (
          <MenuItem onClick={handleSetCover}>
            <ListItemIcon>
              <StarBorderIcon fontSize="small" />
            </ListItemIcon>
            Set as cover photo
          </MenuItem>
        )}
        {!!onDelete && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
